import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useParams } from "react-router-dom";
import { Chargement, EnTetePage, Etiquette, Indicateur, Message, Vide } from "@cabinet/charte";
import {
  declarationsApi,
  dossiersApi,
  ErreurApi,
  type DeclarationVue,
  type PeriodeDeclarativeVue,
  type ResumeDeDeclarationVue,
} from "../api/client";
import { FicheDePiece } from "../composants/Piece";
import { Tiroir } from "../composants/Tiroir";
import { formaterDate, formaterMontant } from "../domaine/formats";
import { useSession } from "../session";

/** Ce qu'on regarde : la déclaration recalculée d'une période, ou une version figée. */
type Selection = { type: "calcul"; date: string } | { type: "figee"; id: string };

const VARIANTE_DU_STATUT: Record<ResumeDeDeclarationVue["statut"], "succes" | "info" | "neutre"> = {
  DEPOSEE: "succes",
  VALIDEE: "info",
  REMPLACEE: "neutre",
};

/**
 * Déclarations de TVA du dossier (EF-1102, EF-1105).
 *
 * À gauche, l'échéancier : une ligne par période, selon la périodicité du
 * dossier, et ce qui en a été fait. À droite, la déclaration de la période
 * choisie — **recalculée depuis les écritures validées** tant qu'elle n'est
 * pas validée, figée ensuite. Les écarts avec la comptabilité sont listés
 * avant la validation et doivent être justifiés ; chacun s'ouvre sur sa pièce.
 */
export function PageDeclarationsTva() {
  const { id = "" } = useParams();
  const dossier = useQuery({ queryKey: ["dossier", id], queryFn: () => dossiersApi.consulter(id) });
  const echeancier = useQuery({
    queryKey: ["declarations-tva", id],
    queryFn: () => declarationsApi.echeancier(id),
    enabled: dossier.data?.tva.assujetti === true,
  });
  const [selection, setSelection] = useState<Selection | null>(null);

  // Par défaut, la première période qui reste à déclarer ; à défaut, la dernière.
  const parDefaut: Selection | null = (() => {
    const periodes = echeancier.data ?? [];
    const aDeclarer = periodes.find((p) => p.declaration == null) ?? periodes[periodes.length - 1];
    return aDeclarer != null ? { type: "calcul", date: aDeclarer.periode.debut } : null;
  })();
  const courante = selection ?? parDefaut;

  const erreur = dossier.error ?? echeancier.error;

  return (
    <>
      <EnTetePage
        surTitre={dossier.data?.raisonSociale ?? "Dossier"}
        titre="Déclarations de TVA"
        description={
          dossier.data?.tva.assujetti
            ? `Régime des ${dossier.data.tva.regime === "DEBITS" ? "débits : la taxe est exigible à la date de la facture" : "encaissements : la taxe est exigible au règlement, lu dans le lettrage"} ; périodicité ${dossier.data.tva.periodicite?.toLowerCase()}. La déclaration se calcule depuis les écritures validées, et se fige à la validation.`
            : "La déclaration se calcule depuis les écritures validées."
        }
      />

      {erreur != null && (
        <Message type="erreur" titre={erreur instanceof ErreurApi ? erreur.titre : "Erreur"}>
          <p style={{ margin: "6px 0 0" }}>{erreur.message}</p>
        </Message>
      )}

      {dossier.data != null && !dossier.data.tva.assujetti && (
        <Vide>Ce dossier n'est pas assujetti à la TVA : il n'a pas de déclaration à établir.</Vide>
      )}

      {echeancier.isPending && dossier.data?.tva.assujetti && <Chargement quoi="l'échéancier fiscal" />}

      {echeancier.data != null && (
        <div className="declarations">
          <Echeancier periodes={echeancier.data} selection={courante} surChoix={setSelection} />
          <div className="declaration-choisie">
            {courante != null ? (
              <Declaration dossier={id} selection={courante} surSelection={setSelection} />
            ) : (
              <Vide>Aucun exercice ouvert : aucune période à déclarer.</Vide>
            )}
          </div>
        </div>
      )}
    </>
  );
}

function Echeancier({
  periodes,
  selection,
  surChoix,
}: {
  periodes: PeriodeDeclarativeVue[];
  selection: Selection | null;
  surChoix: (selection: Selection) => void;
}) {
  return (
    <div className="carte echeancier-fiscal">
      <table>
        <thead>
          <tr>
            <th>Période</th>
            <th>État</th>
            <th className="nombre">Solde</th>
          </tr>
        </thead>
        <tbody>
          {periodes.map(({ periode, declaration }) => {
            const choisie = selection?.type === "calcul" && selection.date === periode.debut;
            return (
              <tr
                key={periode.debut}
                className={`cliquable${choisie ? " choisie" : ""}`}
                tabIndex={0}
                onClick={() => surChoix({ type: "calcul", date: periode.debut })}
                onKeyDown={(e) => e.key === "Enter" && surChoix({ type: "calcul", date: periode.debut })}
              >
                <td>{periode.code}</td>
                <td>
                  {declaration != null ? (
                    <Etiquette variante={VARIANTE_DU_STATUT[declaration.statut]}>
                      {declaration.statutLibelle}
                      {declaration.version > 1 ? ` · v${declaration.version}` : ""}
                    </Etiquette>
                  ) : (
                    <span className="discret-texte">À déclarer</span>
                  )}
                </td>
                <td className="nombre">
                  {declaration == null
                    ? ""
                    : declaration.tvaDue > 0
                      ? `${formaterMontant(declaration.tvaDue)} due`
                      : declaration.creditAReporter > 0
                        ? `${formaterMontant(declaration.creditAReporter)} crédit`
                        : "0,00"}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function Declaration({
  dossier,
  selection,
  surSelection,
}: {
  dossier: string;
  selection: Selection;
  surSelection: (selection: Selection) => void;
}) {
  const { peut } = useSession();
  const cache = useQueryClient();
  const [justification, setJustification] = useState("");
  const [retour, setRetour] = useState<{ type: "succes" | "erreur"; texte: React.ReactNode } | null>(null);
  const [piece, setPiece] = useState<string | null>(null);

  const declaration = useQuery({
    queryKey: ["declaration-tva", dossier, selection],
    queryFn: () =>
      selection.type === "calcul"
        ? declarationsApi.calculer(dossier, selection.date)
        : declarationsApi.consulter(selection.id),
  });

  const rafraichir = () => {
    void cache.invalidateQueries({ queryKey: ["declarations-tva", dossier] });
    void cache.invalidateQueries({ queryKey: ["declaration-tva", dossier] });
  };
  const signaler = (echec: unknown) =>
    setRetour({ type: "erreur", texte: echec instanceof ErreurApi ? echec.message : "L'opération n'a pas abouti." });

  const validation = useMutation({
    mutationFn: (d: DeclarationVue) => declarationsApi.valider(dossier, d.periode.debut, justification.trim() || null),
    onSuccess: (figee) => {
      rafraichir();
      setJustification("");
      setRetour({ type: "succes", texte: `Déclaration ${figee.periode.code} validée en version ${figee.version}.` });
      surSelection({ type: "figee", id: figee.id! });
    },
    onError: signaler,
  });
  const depot = useMutation({
    mutationFn: ({ id, reference }: { id: string; reference: string | null }) => declarationsApi.deposer(id, reference),
    onSuccess: (deposee) => {
      rafraichir();
      setRetour({ type: "succes", texte: `Déclaration ${deposee.periode.code} déposée : elle ne se modifie plus.` });
    },
    onError: signaler,
  });
  const liquidation = useMutation({
    mutationFn: (id: string) => declarationsApi.liquider(id),
    onSuccess: (pieceCreee) =>
      setRetour({
        type: "succes",
        texte: (
          <>
            Écriture de liquidation passée en brouillard.{" "}
            <Link to={`/dossiers/${dossier}/ecritures?piece=${pieceCreee.id}`}>La relire dans le journal</Link>.
          </>
        ),
      }),
    onError: signaler,
  });

  if (declaration.isPending) return <Chargement quoi="la déclaration" />;
  if (declaration.error != null) {
    return (
      <Message type="erreur" titre={declaration.error instanceof ErreurApi ? declaration.error.titre : "Erreur"}>
        <p style={{ margin: "6px 0 0" }}>{declaration.error.message}</p>
      </Message>
    );
  }

  const d = declaration.data;
  const estCalculee = d.id == null;
  const collectees = d.lignes.filter((l) => l.nature === "COLLECTEE");
  const deductibles = d.lignes.filter((l) => l.nature === "DEDUCTIBLE");

  return (
    <>
      <div className="entete-declaration">
        <div>
          <h2>
            TVA {d.periode.code}{" "}
            {estCalculee ? (
              <Etiquette variante="avertissement">Calculée à l'instant</Etiquette>
            ) : (
              <Etiquette variante={VARIANTE_DU_STATUT[d.statut!]}>
                {d.statutLibelle} · version {d.version}
              </Etiquette>
            )}
          </h2>
          <p className="discret-texte">
            Du {formaterDate(d.periode.debut)} au {formaterDate(d.periode.fin)} · régime des{" "}
            {d.regime === "DEBITS" ? "débits" : "encaissements"}
            {d.valideeLe != null && ` · validée le ${new Date(d.valideeLe).toLocaleString("fr-FR")}`}
            {d.deposeeLe != null &&
              ` · déposée le ${new Date(d.deposeeLe).toLocaleString("fr-FR")}${d.referenceDeDepot ? ` (${d.referenceDeDepot})` : ""}`}
          </p>
        </div>
        <div className="jetons">
          {estCalculee && d.courante != null && (
            <button type="button" className="tertiaire" onClick={() => surSelection({ type: "figee", id: d.courante!.id })}>
              Voir la version {d.courante.version} ({d.courante.statutLibelle.toLowerCase()})
            </button>
          )}
          {!estCalculee && (
            <button
              type="button"
              className="tertiaire"
              onClick={() => surSelection({ type: "calcul", date: d.periode.debut })}
            >
              Recalculer depuis les écritures
            </button>
          )}
        </div>
      </div>

      {retour != null && <Message type={retour.type}>{retour.texte}</Message>}

      {estCalculee && d.courante?.statut === "DEPOSEE" && (
        <Message type="avertissement" titre="Période déjà déclarée">
          La version {d.courante.version} a été déposée. Valider ce calcul produira une déclaration rectificative, conservée
          à côté de l'originale.
        </Message>
      )}

      <div className="indicateurs">
        <Indicateur libelle="Chiffre d'affaires imposable" valeur={formaterMontant(d.chiffreDAffaires)} detail={d.devise} />
        <Indicateur libelle="TVA exigible" valeur={formaterMontant(d.tvaExigible)} />
        <Indicateur libelle="TVA déductible" valeur={formaterMontant(d.tvaDeductible)} />
        <Indicateur libelle="Crédit antérieur" valeur={formaterMontant(d.creditAnterieur)} />
        {d.creditAReporter > 0 ? (
          <Indicateur libelle="Crédit à reporter" valeur={formaterMontant(d.creditAReporter)} />
        ) : (
          <Indicateur libelle="TVA due" valeur={formaterMontant(d.tvaDue)} />
        )}
      </div>

      <div className="carte">
        <h3>Opérations imposables</h3>
        <TableDesLignes lignes={collectees} vide="Aucune opération imposable exigible dans la période." />
        <h3>Déductions</h3>
        <TableDesLignes lignes={deductibles} vide="Aucune déduction dans la période." />
      </div>

      {d.anomalies.length > 0 && (
        <div className="carte">
          <h3>
            Écarts avec la comptabilité <Etiquette variante="avertissement">{d.anomalies.length}</Etiquette>
          </h3>
          <p className="discret-texte">
            Ce que les écritures portent et que la déclaration ne peut pas reprendre tel quel. Une déclaration qui en présente
            ne se valide qu'avec une justification (EF-1105).
          </p>
          <div className="tableau-conteneur">
            <table>
              <thead>
                <tr>
                  <th>Nature</th>
                  <th>Date</th>
                  <th>Détail</th>
                  <th className="nombre">Montant</th>
                </tr>
              </thead>
              <tbody>
                {d.anomalies.map((a, index) => (
                  <tr
                    key={index}
                    className={a.piece != null ? "cliquable" : undefined}
                    onClick={() => a.piece != null && setPiece(a.piece)}
                  >
                    <td>{a.natureLibelle}</td>
                    <td>{formaterDate(a.date)}</td>
                    <td>{a.detail}</td>
                    <td className="nombre">{a.montant != null ? formaterMontant(a.montant) : ""}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {d.justification != null && (
            <p>
              <strong>Justification :</strong> {d.justification}
            </p>
          )}
        </div>
      )}

      <div className="carte">
        <h3>
          Relevé des déductions{" "}
          {!estCalculee && d.releve.length > 0 && (
            <a className="discret-texte" href={declarationsApi.releveCsv(d.id!)}>
              Exporter en CSV
            </a>
          )}
        </h3>
        {d.releve.length === 0 ? (
          <Vide>Aucune facture déductible dans la période.</Vide>
        ) : (
          <div className="tableau-conteneur">
            <table>
              <thead>
                <tr>
                  <th className="nombre">N°</th>
                  <th>Facture</th>
                  <th>Date</th>
                  <th>Fournisseur</th>
                  <th>IF · ICE</th>
                  <th>Désignation</th>
                  <th className="nombre">Taux</th>
                  <th className="nombre">HT</th>
                  <th className="nombre">TVA</th>
                  <th className="nombre">TTC</th>
                  <th>Règlement</th>
                </tr>
              </thead>
              <tbody>
                {d.releve.map((r, index) => (
                  <tr key={`${r.piece}-${r.code}-${index}`} className="cliquable" onClick={() => setPiece(r.piece)}>
                    <td className="nombre">{index + 1}</td>
                    <td>{r.reference ?? "—"}</td>
                    <td>{formaterDate(r.dateFacture)}</td>
                    <td>
                      {r.raisonSociale ?? r.tiers ?? "—"}
                      {r.raisonSociale == null && r.tiers != null && (
                        <span className="discret-texte"> · sans fiche</span>
                      )}
                    </td>
                    <td className="discret-texte">
                      {r.identifiantFiscal ?? "—"} · {r.ice ?? <Etiquette variante="avertissement">sans ICE</Etiquette>}
                    </td>
                    <td>{r.designation}</td>
                    <td className="nombre">{r.taux} %</td>
                    <td className="nombre">{formaterMontant(r.base)}</td>
                    <td className="nombre">{formaterMontant(r.taxe)}</td>
                    <td className="nombre">{formaterMontant(r.ttc)}</td>
                    <td className="discret-texte">
                      {r.modeDeReglement ?? ""} {formaterDate(r.dateDeReglement)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <div className="barre-actions">
        {estCalculee && peut("PREPARER_DECLARATION") && (
          <>
            {d.anomalies.length > 0 && (
              <textarea
                className="justification"
                value={justification}
                onChange={(e) => setJustification(e.target.value)}
                placeholder="Justification des écarts, exigée pour valider"
                rows={2}
              />
            )}
            <button
              type="button"
              className="principal"
              disabled={validation.isPending || (d.anomalies.length > 0 && justification.trim() === "")}
              onClick={() => {
                setRetour(null);
                validation.mutate(d);
              }}
            >
              {d.courante?.statut === "DEPOSEE" ? "Valider la rectificative" : "Valider la déclaration"}
            </button>
          </>
        )}
        {!estCalculee && d.statut === "VALIDEE" && peut("DEPOSER_DECLARATION") && (
          <button
            type="button"
            className="principal"
            disabled={depot.isPending}
            onClick={() => {
              const reference = window.prompt("Référence du dépôt (accusé de réception), facultative :", "");
              if (reference == null) return;
              setRetour(null);
              depot.mutate({ id: d.id!, reference: reference.trim() || null });
            }}
          >
            Constater le dépôt
          </button>
        )}
        {!estCalculee && d.statut !== "REMPLACEE" && peut("SAISIR_ECRITURE") && (
          <button
            type="button"
            className="tertiaire"
            disabled={liquidation.isPending}
            onClick={() => {
              setRetour(null);
              liquidation.mutate(d.id!);
            }}
          >
            Passer l'écriture de liquidation
          </button>
        )}
      </div>

      {piece != null && (
        <Tiroir titre="Pièce" surTitre={`Déclaration ${d.periode.code}`} surFermeture={() => setPiece(null)}>
          <FicheDePiece piece={piece} dossier={dossier} />
        </Tiroir>
      )}
    </>
  );
}

function TableDesLignes({ lignes, vide }: { lignes: DeclarationVue["lignes"]; vide: string }) {
  if (lignes.length === 0) return <p className="discret-texte">{vide}</p>;
  const ecart = lignes.some((l) => Math.abs(l.ecart) >= 0.01);
  return (
    <div className="tableau-conteneur">
      <table>
        <thead>
          <tr>
            <th>Rubrique</th>
            <th className="nombre">Taux</th>
            <th className="nombre">Base</th>
            <th className="nombre">Taxe</th>
            {ecart && <th className="nombre">Au taux</th>}
            {ecart && <th className="nombre">Écart</th>}
          </tr>
        </thead>
        <tbody>
          {lignes.map((l) => (
            <tr key={`${l.compteDeTva}-${l.code}`}>
              <td>
                {l.libelleDuCompte} <span className="discret-texte">· {l.compteDeTva}</span>
              </td>
              <td className="nombre">{l.taux} %</td>
              <td className="nombre">{formaterMontant(l.base)}</td>
              <td className="nombre">{formaterMontant(l.taxe)}</td>
              {ecart && <td className="nombre">{formaterMontant(l.taxeCalculee)}</td>}
              {ecart && <td className={`nombre${Math.abs(l.ecart) >= 0.01 ? " ecart" : ""}`}>{formaterMontant(l.ecart)}</td>}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
